import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../app/axios";
import {
  getHomepageSectionDefinition,
  mergeHomepagePayload,
} from "../content/homepageSections";

type HomepageSectionDocument = {
  sectionKey: string;
  isActive?: boolean;
  payload?: unknown;
};

const fetchHomepageSection = async (sectionKey: string) => {
  try {
    const { data } = await api.get(`/homepage-sections/${sectionKey}`);
    return data as HomepageSectionDocument;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export function useHomepageSectionContent<T extends Record<string, unknown>>(
  sectionKey: string,
  fallback: T
) {
  const definition = getHomepageSectionDefinition(sectionKey);

  const query = useQuery({
    queryKey: ["homepageSection", sectionKey],
    queryFn: () => fetchHomepageSection(sectionKey),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const content = useMemo(
    () => mergeHomepagePayload(fallback, query.data?.payload),
    [fallback, query.data?.payload]
  );

  return {
    ...query,
    content,
    isInactive: query.data?.isActive === false,
    definition,
  };
}
